/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

declare const window: Window & { dataLayer: Record<string, unknown>[] };

/**
 * Data structure for recommendation tracking
 */
export interface RecommendationData {
    was_recommended: boolean;
    recommended_location: string;
}

/**
 * Checkout event types that may contain recommendation data in the dataLayer
 */
export const CHECKOUT_EVENT_TYPES = [
    'begin_checkout',
    'view_cart',
    'add_payment_info',
] as const;

export type CheckoutEventType = (typeof CHECKOUT_EVENT_TYPES)[number];

/**
 * Checks if a referrer string indicates a recommendation-based navigation.
 *
 * Recommendation referrers have format: "model:pageReferrer:locationId:nthItem:campground-v1:campgroundPage"
 * where model is something like "nearby", "nearby2", etc.
 * Regular campground visits only have "campground-v1:campgroundPage" which should NOT be treated as a recommendation.
 *
 * @param referrer - The referrer string to check
 * @returns true if the referrer indicates a recommendation, false otherwise
 */
export const isRecommendationReferrer = (referrer: string): boolean => {
    if (!referrer) return false;

    return (
        referrer.includes('nearby') ||
        referrer.includes('recommendation') ||
        (referrer.split(':').length > 2 && // Has more than just "campground-v1:campgroundPage"
            !referrer.startsWith('campground-v')) // Doesn't start with just campground version
    );
};

/**
 * Searches the dataLayer for recent checkout events that contain recommendation data
 * for a specific product.
 *
 * This fallback helps when storage is empty but earlier checkout steps had recommendation data.
 *
 * @param productId - The full product ID (e.g., "facilityId|inventoryId")
 * @param facilityId - The facility ID extracted from the product
 * @returns RecommendationData if found, null otherwise
 */
export const findRecommendationDataInDataLayer = (
    productId: string,
    facilityId: string
): RecommendationData | null => {
    if (!window.dataLayer) return null;

    try {
        // Look for recent checkout events that had recommendation data
        const recentCheckoutEvents = window.dataLayer
            .slice()
            .reverse()
            .filter(
                (event: any) =>
                    event.event &&
                    CHECKOUT_EVENT_TYPES.includes(event.event) &&
                    event.ecommerce?.items?.some(
                        (item: any) =>
                            item.item_id === productId ||
                            item.item_id === facilityId ||
                            (item.item_id && item.item_id.startsWith(`${facilityId}|`)) ||
                            item.facility_id === facilityId
                    ) &&
                    event.ecommerce.items.some((item: any) => item.was_recommended)
            );

        if (recentCheckoutEvents.length > 0) {
            const eventWithRecommendation = recentCheckoutEvents[0];
            // Find the item that matches our product AND has recommendation data
            const itemWithRecommendation = eventWithRecommendation.ecommerce.items.find(
                (item: any) =>
                    item.was_recommended &&
                    (item.item_id === productId ||
                        item.item_id === facilityId ||
                        (item.item_id && item.item_id.startsWith(`${facilityId}|`)) ||
                        item.facility_id === facilityId)
            );

            if (itemWithRecommendation) {
                return {
                    was_recommended: itemWithRecommendation.was_recommended,
                    recommended_location:
                        itemWithRecommendation.recommended_location ||
                        'Explore Nearby Campgrounds',
                };
            }
        }
    } catch (e) {
        // Silently fail if dataLayer parsing fails
    }

    return null;
};

/**
 * Determines the recommended location string based on the referrer pattern.
 *
 * @param referrer - The referrer string to analyze
 * @returns The appropriate location string
 */
export const getRecommendedLocationFromReferrer = (referrer: string): string => {
    if (referrer.includes('nearby')) {
        return 'Explore Nearby Campgrounds';
    }
    if (referrer.includes('cart')) {
        return 'Cart Suggestions';
    }
    if (referrer.includes('campgroundPage')) {
        return 'Campground Page';
    }
    // Default fallback
    return 'Explore Nearby Campgrounds';
};

/**
 * Creates recommendation data from a referrer string.
 *
 * @param referrer - The referrer string
 * @returns RecommendationData with location based on referrer pattern
 */
export const createRecommendationDataFromReferrer = (
    referrer: string
): RecommendationData => {
    return {
        was_recommended: true,
        recommended_location: getRecommendedLocationFromReferrer(referrer),
    };
};

// cspell:ignore ecommerce
